/**
 * parseParkedSecurityStatement — lightweight lexical scan that pulls the
 * security-principal kind + name out of the parked CREATE / ALTER / DROP
 * {LOGIN|USER|ROLE} statement so the inline `[Edit Security]` popover can
 * seed its form.
 *
 * DSE2's `extractDdlStructure` does not expose security arms; the inline
 * path scans the parked statement directly. The parser captures only the
 * shape and identity — the actual options (password, default schema, role
 * attributes, …) are not attempted because per-engine variation is too
 * wide. The form starts with the seeded name + kind; the user fills the
 * rest.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice F1.
 */

const IDENT = '[A-Za-z_][A-Za-z0-9_]*|"[^"]+"|`[^`]+`|\\[[^\\]]+\\]';
const SECURITY_NAME_RE = new RegExp(
  `^(${IDENT})(?:\\s*@\\s*'([^']+)')?`,
);
const QUOTED_NAME_RE = /^'([^']+)'(?:\s*@\s*'([^']+)')?/;

export type SecurityKind = 'login' | 'user' | 'role';

export interface ParsedParkedSecurity {
  readonly action: 'create' | 'alter' | 'drop';
  readonly kind: SecurityKind;
  readonly name: string;
  readonly quoted: boolean;
  /** mysql `'name'@'host'` host part. Undefined when not present. */
  readonly mysqlHost?: string;
  /** `IF NOT EXISTS` (CREATE) / `IF EXISTS` (DROP). */
  readonly ifNotExists?: boolean;
  readonly ifExists?: boolean;
}

function unquoteIdent(raw: string): { name: string; quoted: boolean } {
  if (raw.length >= 2) {
    const first = raw.charAt(0);
    const last = raw.charAt(raw.length - 1);
    if ((first === '"' && last === '"') || (first === '`' && last === '`')) {
      return { name: raw.slice(1, -1), quoted: true };
    }
    if (first === '[' && last === ']') {
      return { name: raw.slice(1, -1), quoted: true };
    }
  }
  return { name: raw, quoted: false };
}

function skipSpaces(s: string, start: number): number {
  let i = start;
  while (i < s.length && /\s/.test(s.charAt(i))) i++;
  return i;
}

function consumeKeyword(s: string, start: number, keyword: string): number {
  const upper = s.slice(start, start + keyword.length).toUpperCase();
  if (upper !== keyword) return -1;
  const next = s.charAt(start + keyword.length);
  if (next && /[A-Za-z0-9_]/.test(next)) return -1;
  return start + keyword.length;
}

function consumePhrase(s: string, start: number, phrase: string): number {
  // Matches a sequence of whitespace-separated words case-insensitively.
  const words = phrase.split(/\s+/);
  let cursor = start;
  for (let i = 0; i < words.length; i++) {
    cursor = skipSpaces(s, cursor);
    const next = consumeKeyword(s, cursor, words[i]!);
    if (next === -1) return -1;
    cursor = next;
  }
  return cursor;
}

export function parseParkedSecurityStatement(
  stmtText: string,
): ParsedParkedSecurity | null {
  const trimmed = stmtText.trim();
  const upper = trimmed.toUpperCase();

  let action: 'create' | 'alter' | 'drop';
  let cursor: number;
  if (upper.startsWith('CREATE')) { action = 'create'; cursor = 'CREATE'.length; }
  else if (upper.startsWith('ALTER')) { action = 'alter'; cursor = 'ALTER'.length; }
  else if (upper.startsWith('DROP')) { action = 'drop'; cursor = 'DROP'.length; }
  else return null;

  cursor = skipSpaces(trimmed, cursor);

  // OR ALTER (mssql) — only on CREATE
  if (action === 'create') {
    const orAlter = consumePhrase(trimmed, cursor, 'OR ALTER');
    if (orAlter !== -1) cursor = orAlter;
  }
  cursor = skipSpaces(trimmed, cursor);

  let kind: SecurityKind;
  let next = consumeKeyword(trimmed, cursor, 'LOGIN');
  if (next !== -1) { kind = 'login'; cursor = next; }
  else {
    next = consumeKeyword(trimmed, cursor, 'USER');
    if (next !== -1) { kind = 'user'; cursor = next; }
    else {
      next = consumeKeyword(trimmed, cursor, 'ROLE');
      if (next !== -1) { kind = 'role'; cursor = next; }
      else return null;
    }
  }
  cursor = skipSpaces(trimmed, cursor);

  let ifNotExists = false;
  let ifExists = false;
  if (action === 'create') {
    const ine = consumePhrase(trimmed, cursor, 'IF NOT EXISTS');
    if (ine !== -1) { ifNotExists = true; cursor = ine; cursor = skipSpaces(trimmed, cursor); }
  } else if (action === 'drop') {
    const ie = consumePhrase(trimmed, cursor, 'IF EXISTS');
    if (ie !== -1) { ifExists = true; cursor = ie; cursor = skipSpaces(trimmed, cursor); }
  }

  // Try mysql-style 'name'@'host' first.
  const tail = trimmed.slice(cursor);
  const mysqlMatch = QUOTED_NAME_RE.exec(tail);
  if (mysqlMatch) {
    return {
      action,
      kind,
      name: mysqlMatch[1] ?? '',
      quoted: true,
      ...(mysqlMatch[2] ? { mysqlHost: mysqlMatch[2] } : {}),
      ...(ifNotExists ? { ifNotExists: true } : {}),
      ...(ifExists ? { ifExists: true } : {}),
    };
  }

  const m = SECURITY_NAME_RE.exec(tail);
  if (!m) return null;
  const ident = unquoteIdent(m[1]!);
  return {
    action,
    kind,
    name: ident.name,
    quoted: ident.quoted,
    ...(m[2] ? { mysqlHost: m[2] } : {}),
    ...(ifNotExists ? { ifNotExists: true } : {}),
    ...(ifExists ? { ifExists: true } : {}),
  };
}
